import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
export type PermissionState = 'unset' | 'granted' | 'denied';
interface SessionValue {
  /** Prototype only. REQUIRES BACKEND: real authentication and session tokens. */
  authenticated: boolean;
  signIn: () => void;
  signOut: () => void;
  /** Onboarding is never repeated once completed. */
  onboarded: boolean;
  completeOnboarding: () => void;
  goals: string[];
  toggleGoal: (id: string) => void;
  activities: string[];
  toggleActivity: (id: string) => void;
  permissions: Record<string, PermissionState>;
  setPermission: (id: string, state: PermissionState) => void;
}
const SessionContext = createContext<SessionValue | null>(null);
export function SessionProvider({
  /** `returning` opens straight on Home; `newUser` walks the full entry flow. */
  initialSession = 'returning',
  children
}: {
  initialSession?: 'returning' | 'newUser';
  children: React.ReactNode;
}) {
  const returning = initialSession === 'returning';
  const [authenticated, setAuthenticated] = useState(returning);
  const [onboarded, setOnboarded] = useState(returning);
  const [goals, setGoals] = useState<string[]>([]);
  const [activities, setActivities] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<Record<string, PermissionState>>({
    notifications: 'unset',
    camera: 'unset',
    location: 'unset'
  });
  const toggle = useCallback((setter: React.Dispatch<React.SetStateAction<string[]>>, id: string) => setter(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]), []);
  const value = useMemo<SessionValue>(() => ({
    authenticated,
    signIn: () => setAuthenticated(true),
    signOut: () => {
      setAuthenticated(false);
      setOnboarded(false);
    },
    onboarded,
    completeOnboarding: () => setOnboarded(true),
    goals,
    toggleGoal: id => toggle(setGoals, id),
    activities,
    toggleActivity: id => toggle(setActivities, id),
    permissions,
    setPermission: (id, state) => setPermissions(prev => ({
      ...prev,
      [id]: state
    }))
  }), [authenticated, onboarded, goals, activities, permissions, toggle]);
  return <SessionContext.Provider value={value} data-id="element-1517" data-mp="24hmrs4ea06v1">{children}</SessionContext.Provider>;
}
export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}